import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './main.css';
import './styles/header.css';
import './styles/hero.css';
import './styles/sections.css';
import './styles/modal.css';

// Import assets
import logo from './assets/logo.svg';
import screen1 from './assets/screen_1.jpg';
import screen2 from './assets/screen_2.jpg';
import google_play from './assets/google_play.png';
import app_store from './assets/app_store.png';

// Import components and data
import JoinForm, { SUPPORT_EMAIL, SUPPORT_PHONE, SUPPORT_PHONE_LABEL } from './JoinForm.js';
import LanguageSwitcher from './components/LanguageSwitcher.js';
import Modal from './components/Modal.js';
import Receipt from './components/Receipt.js';
import Ticker from './components/Ticker.js';
import {
  ArrowIcon,
  BagIcon,
  GrowthIcon,
  MailIcon,
  PhoneIcon,
  PinIcon,
  ScooterIcon,
  ShieldIcon,
} from './components/Icons.js';
import useReveal from './hooks/useReveal.js';
import textData from './language-pack.json';

const SECTIONS = ['home', 'about', 'join', 'contact'];
const LANGUAGE_KEY = 'app_language';

const PLAY_URL = 'https://play.google.com/store/apps/details?id=az.foody';
const APP_STORE_URL = 'https://apps.apple.com/az/app/foody-pulsuz-yemək-çatdırılması/id6478478153';
const PARTNER_PLAY_URL = 'https://play.google.com/store/apps/details?id=az.foody.partner';

const HERO_POINT_ICONS = [PinIcon, PhoneIcon, ShieldIcon];
const JOIN_POINT_ICONS = [GrowthIcon, BagIcon, ScooterIcon];

const readSavedLanguage = () => {
  try {
    const saved = localStorage.getItem(LANGUAGE_KEY);
    return saved && textData[saved] ? saved : 'AZ';
  } catch {
    return 'AZ';
  }
};

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
const isAndroid = () => /android/i.test(navigator.userAgent || '');
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent || '') && !window.MSStream;

const StoreButtons = ({ className = '' }) => (
  <div className={`store-buttons ${className}`}>
    <a href={PLAY_URL} target="_blank" rel="noopener noreferrer" className="store-button">
      <img src={google_play} alt="Get it on Google Play" width="248" height="74" />
    </a>
    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="store-button">
      <img src={app_store} alt="Download on the App Store" width="290" height="75" />
    </a>
  </div>
);

// --- Main App Component ---
const App = () => {
  const [language, setLanguage] = useState(readSavedLanguage);
  const [activeSection, setActiveSection] = useState('home');
  const [modalKind, setModalKind] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [fadeTick, setFadeTick] = useState(0);
  const [storePulse, setStorePulse] = useState(0);
  const appRef = useRef(null);
  const topSentinelRef = useRef(null);
  const progressRef = useRef(null);
  const heroVisualRef = useRef(null);
  const [isHeroReady, setIsHeroReady] = useState(false);

  const currentText = textData[language] || textData.AZ;
  const year = new Date().getFullYear();

  useReveal(language);

  const modalContent = useMemo(() => {
    switch (modalKind) {
      case 'terms':
        return { title: currentText.footer[0], text: currentText.modal.terms };
      case 'privacy':
        return { title: currentText.footer[1], text: currentText.modal.privacy };
      case 'deleteAccount':
        return currentText.modal.deleteAccount
          ? { title: currentText.modal.deleteAccount.title, text: currentText.modal.deleteAccount.content }
          : null;
      default:
        return null;
    }
  }, [modalKind, currentText]);

  // --- LANGUAGE ---
  useEffect(() => {
    document.documentElement.lang = language.toLowerCase();
  }, [language]);

  const changeLanguage = useCallback(
    (code) => {
      if (code === language) return;
      try {
        localStorage.setItem(LANGUAGE_KEY, code);
      } catch {
        // Private mode can refuse storage; the choice still applies for this visit.
      }
      setLanguage(code);
      setFadeTick((tick) => tick + 1);
    },
    [language]
  );

  // --- MODAL & SCROLL LOGIC ---
  const openModal = useCallback((kind) => {
    setModalKind(kind);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const scrollToSection = useCallback((sectionId) => {
    const target = document.getElementById(sectionId);
    if (!target) return;
    document.documentElement.classList.remove('is-locked');
    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }, []);

  const handleNavClick = (sectionId) => (event) => {
    event.preventDefault();
    setIsMenuOpen(false);
    scrollToSection(sectionId);
  };

  const storeUrl = useMemo(() => (isAndroid() ? PLAY_URL : isIOS() ? APP_STORE_URL : null), []);

  const handleGetApp = (event) => {
    if (storeUrl) return;
    event.preventDefault();
    setIsMenuOpen(false);
    scrollToSection('home');
    setStorePulse((tick) => tick + 1);
  };

  // URL PARAMETER HANDLING — the mobile apps deep-link here with ?open=...
  useEffect(() => {
    const pageToOpen = new URLSearchParams(window.location.search).get('open');
    if (!pageToOpen) return undefined;

    const timer = setTimeout(() => {
      switch (pageToOpen) {
        case 'home':
        case 'about':
        case 'join':
        case 'contact':
          scrollToSection(pageToOpen);
          break;
        case 'userTerms':
          openModal('terms');
          break;
        case 'privacyPolicy':
          openModal('privacy');
          break;
        case 'deleteAccount':
          openModal('deleteAccount');
          break;
        case 'getApp':
          if (isAndroid()) window.location.href = PLAY_URL;
          else if (isIOS()) window.location.href = APP_STORE_URL;
          break;
        case 'getPartnerApp':
          if (isAndroid()) window.location.href = PARTNER_PLAY_URL;
          else if (isIOS()) window.location.href = APP_STORE_URL;
          break;
        default:
          break;
      }
      window.history.replaceState(null, '', window.location.pathname);
    }, 300);
    return () => clearTimeout(timer);
  }, [openModal, scrollToSection]);

  // The phones' hover tilt waits until the phones have risen and the receipt paper is fully
  // printed out; the receipt rows and the stamp may still be animating after that.
  useEffect(() => {
    const node = heroVisualRef.current;
    if (!node) return undefined;
    let cancelled = false;
    const markReady = () => !cancelled && setIsHeroReady(true);

    if (!node.getAnimations) {
      const timer = setTimeout(markReady, 2200);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }

    const intro = node
      .getAnimations({ subtree: true })
      .filter((animation) => ['phoneIn', 'print'].includes(animation.animationName));
    Promise.all(intro.map((animation) => animation.finished)).then(markReady, markReady);
    return () => {
      cancelled = true;
    };
  }, []);

  // EFFECT FOR SCROLL SPY
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 }
    );
    SECTIONS.forEach((id) => {
      const node = document.getElementById(id);
      if (node) observer.observe(node);
    });
    return () => observer.disconnect();
  }, []);

  // The header background appears once a sentinel at the very top leaves the viewport,
  // so scrolling itself runs no script for it
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(([entry]) => setIsScrolled(!entry.isIntersecting));
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Looping hero animations pause while the hero is out of view (hero.css)
  useEffect(() => {
    const app = appRef.current;
    const hero = document.getElementById('home');
    if (!app || !hero || !('IntersectionObserver' in window)) return undefined;
    const observer = new IntersectionObserver(([entry]) =>
      app.toggleAttribute('data-hero-offscreen', !entry.isIntersecting)
    );
    observer.observe(hero);
    return () => observer.disconnect();
  }, []);

  // Reading progress is a scroll-driven CSS animation where supported (header.css);
  // other browsers scale it from a scroll listener, at most once per frame
  useEffect(() => {
    if (window.CSS?.supports?.('animation-timeline: scroll()')) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${progress})`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Lock page scroll behind the menu or a modal
  useEffect(() => {
    document.documentElement.classList.toggle('is-locked', isModalOpen || isMenuOpen);
    return () => document.documentElement.classList.remove('is-locked');
  }, [isModalOpen, isMenuOpen]);

  // Menu closes on Escape and when the viewport grows into the desktop layout
  useEffect(() => {
    if (!isMenuOpen) return undefined;
    const onKeyDown = (event) => event.key === 'Escape' && setIsMenuOpen(false);
    const desktop = window.matchMedia?.('(min-width: 1024px)');
    const onChange = (event) => event.matches && setIsMenuOpen(false);
    document.addEventListener('keydown', onKeyDown);
    desktop?.addEventListener?.('change', onChange);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      desktop?.removeEventListener?.('change', onChange);
    };
  }, [isMenuOpen]);

  const titleWords = currentText.mainTitle.split(' ');
  const aboutParagraphs = currentText.aboutText.split('\n\n');
  const zero = language === 'EN' ? '0.00 ₼' : '0,00 ₼';

  return (
    <div ref={appRef} className="app">
      <span ref={topSentinelRef} className="scroll-sentinel" aria-hidden="true" />
      <div className="backdrop" aria-hidden="true">
        <span className="backdrop__glow backdrop__glow--tomato" />
        <span className="backdrop__glow backdrop__glow--saffron" />
      </div>

      <header className={`site-header${isScrolled ? ' is-scrolled' : ''}${isMenuOpen ? ' is-menu-open' : ''}`}>
        <div className="container site-header__bar">
          <a href="#home" className="brand" onClick={handleNavClick('home')}>
            <img src={logo} alt="" className="brand__mark" width="44" height="44" />
            <span className="brand__text">
              <span className="brand__name">Foody</span>
              <span className="brand__tagline">{currentText.tagline}</span>
            </span>
          </a>

          <nav className="site-nav" aria-label="Foody">
            {SECTIONS.map((sectionId, index) => (
              <a
                key={sectionId}
                href={`#${sectionId}`}
                className="site-nav__link"
                aria-current={activeSection === sectionId ? 'true' : undefined}
                onClick={handleNavClick(sectionId)}
              >
                {currentText.header[index]}
              </a>
            ))}
          </nav>

          <div className="site-header__actions">
            <div className="site-header__lang">
              <LanguageSwitcher language={language} onChange={changeLanguage} label={currentText.a11y.language} />
            </div>
            <a
              href={storeUrl || '#home'}
              target={storeUrl ? '_blank' : undefined}
              rel={storeUrl ? 'noopener noreferrer' : undefined}
              className="button button--primary button--small site-header__cta"
              onClick={handleGetApp}
            >
              {currentText.headerCta}
            </a>
            <button
              type="button"
              className="menu-toggle"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label={currentText.a11y.menu}
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <span className="menu-toggle__line" />
              <span className="menu-toggle__line" />
            </button>
          </div>
        </div>
        <div ref={progressRef} className="scroll-progress" aria-hidden="true" />
      </header>

      <div id="mobile-menu" className="mobile-menu" data-open={isMenuOpen} inert={!isMenuOpen}>
        <nav className="mobile-menu__nav" aria-label="Foody">
          {SECTIONS.map((sectionId, index) => (
            <a
              key={sectionId}
              href={`#${sectionId}`}
              className="mobile-menu__link"
              style={{ '--i': index }}
              aria-current={activeSection === sectionId ? 'true' : undefined}
              onClick={handleNavClick(sectionId)}
            >
              <span className="mobile-menu__index">0{index + 1}</span>
              {currentText.header[index]}
            </a>
          ))}
        </nav>
        <div className="mobile-menu__footer" style={{ '--i': SECTIONS.length }}>
          <LanguageSwitcher
            variant="grid"
            language={language}
            onChange={changeLanguage}
            label={currentText.a11y.language}
          />
          <StoreButtons className="mobile-menu__stores" />
        </div>
      </div>

      <Modal
        isOpen={isModalOpen && Boolean(modalContent)}
        onClose={closeModal}
        title={modalContent?.title}
        text={modalContent?.text}
        closeLabel={currentText.a11y.close}
      />

      <main id="main" className="lang-fade" data-fade={fadeTick % 2}>
        <section id="home" className="hero">
          <div className="container hero__grid">
            <div className="hero__copy">
              <p className="hero__badge">
                <span className="hero__badge-dot" aria-hidden="true" />
                {currentText.heroBadge}
              </p>
              <h1
                key={language}
                className="hero__title"
                style={{ '--longest': Math.max(...titleWords.map((word) => word.length)) }}
              >
                {titleWords.map((word, index) => (
                  <Fragment key={index}>
                    <span
                      className={`hero__word${index === titleWords.length - 1 ? ' hero__word--mark' : ''}`}
                      style={{ '--w': index }}
                    >
                      {word}
                    </span>
                    {index < titleWords.length - 1 ? ' ' : ''}
                  </Fragment>
                ))}
              </h1>
              <p className="hero__lead">{currentText.description}</p>
              <StoreButtons className={`hero__stores${storePulse ? ' is-pulsing' : ''}`} key={storePulse} />
              <ul className="hero__points">
                {currentText.heroPoints.map((point, index) => {
                  const PointIcon = HERO_POINT_ICONS[index];
                  return (
                    <li key={index} className="hero__point" style={{ '--i': index }}>
                      <PointIcon size={18} />
                      {point}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div ref={heroVisualRef} className={`hero__visual${isHeroReady ? ' is-ready' : ''}`}>
              <div className="phones" aria-hidden="true">
                <div className="phone phone--back">
                  <div className="phone__body">
                    <div className="phone__screen">
                      <img src={screen2} alt="" width="719" height="1543" />
                    </div>
                  </div>
                </div>
                <div className="phone phone--front">
                  <div className="phone__body">
                    <div className="phone__screen">
                      <img src={screen1} alt="" width="719" height="1543" />
                    </div>
                  </div>
                </div>
              </div>
              <Receipt text={currentText.receipt} language={language} />
            </div>
          </div>
        </section>

        <Ticker
          deliveryLabel={currentText.receipt.delivery}
          serviceLabel={currentText.receipt.service}
          amount={zero}
          language={language}
        />

        <section id="about" className="section about">
          <div className="container about__grid">
            <div className="about__intro" data-reveal="">
              <span className="section-index">01</span>
              <h2 className="section-title">{currentText.aboutTitle}</h2>
              <p className="about__lead">{aboutParagraphs[0]}</p>
            </div>
            <ol className="about__list">
              {aboutParagraphs.slice(1).map((paragraph, index) => (
                <li key={index} className="about__item" data-reveal="" style={{ '--d': `${index * 90}ms` }}>
                  <span className="about__num">0{index + 1}</span>
                  <p>{paragraph}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section id="join" className="section join">
          <div className="container">
            <div className="join__panel">
              <div className="join__copy" data-reveal="">
                <span className="section-index section-index--light">02</span>
                <h2 className="section-title section-title--light">{currentText.joinTitle}</h2>
                <p className="join__text">{currentText.joinText}</p>
                <ul className="join__points">
                  {currentText.joinPoints.map((point, index) => {
                    const PointIcon = JOIN_POINT_ICONS[index];
                    return (
                      <li key={index} className="join__point">
                        <span className="join__point-icon">
                          <PointIcon size={20} />
                        </span>
                        {point}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="join__form" data-reveal="" style={{ '--d': '120ms' }}>
                <JoinForm currentText={currentText} />
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="section contact">
          <div className="container contact__grid">
            <div className="contact__head" data-reveal="">
              <span className="section-index">03</span>
              <h2 className="section-title">{currentText.contactTitle}</h2>
              <p className="contact__text">{currentText.contactText}</p>
            </div>
            <div className="contact__cards">
              <a className="contact-card" href={`tel:${SUPPORT_PHONE}`} data-reveal="" style={{ '--d': '60ms' }}>
                <span className="contact-card__icon">
                  <PhoneIcon size={24} />
                </span>
                <span className="contact-card__label">{currentText.contactCall}</span>
                <span className="contact-card__value">{SUPPORT_PHONE_LABEL}</span>
                <ArrowIcon size={22} className="contact-card__arrow" />
              </a>
              <a className="contact-card" href={`mailto:${SUPPORT_EMAIL}`} data-reveal="" style={{ '--d': '140ms' }}>
                <span className="contact-card__icon">
                  <MailIcon size={24} />
                </span>
                <span className="contact-card__label">{currentText.contactMail}</span>
                <span className="contact-card__value">{SUPPORT_EMAIL}</span>
                <ArrowIcon size={22} className="contact-card__arrow" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container site-footer__top">
          <div className="site-footer__brand">
            <span className="site-footer__word" aria-hidden="true">
              Foody
            </span>
            <p className="site-footer__lead">{currentText.description}</p>
          </div>
          <StoreButtons className="site-footer__stores" />
        </div>
        <div className="container site-footer__bottom">
          <p className="site-footer__copy">{currentText.copyright.replace('{year}', year)}</p>
          <nav className="site-footer__links">
            <button type="button" className="site-footer__link" onClick={() => openModal('terms')}>
              {currentText.footer[0]}
            </button>
            <button type="button" className="site-footer__link" onClick={() => openModal('privacy')}>
              {currentText.footer[1]}
            </button>
            <a href="#join" className="site-footer__link" onClick={handleNavClick('join')}>
              {currentText.footer[2]}
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default App;
